/**
 * /models/:modelId — 车型详情（Phase 4 §4.3）。
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Button, Empty, Skeleton, Tooltip } from 'antd';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import RiskTag from '@/components/RiskTag';
import StatCard from '@/components/StatCard';
import KeywordCloud from '@/components/KeywordCloud';
import QuoteCard from '@/components/QuoteCard';
import { api } from '@/lib/api';
import { useBreakpoint } from '@/lib/breakpoints';
import { checkSparsity } from '@/lib/sparsity';
import { DATA_NOT_READY_HINT, isWeekDataReady, type WeekStatus } from '@/lib/weekData';
import { useWeekStore } from '@/stores/weekStore';

interface ModelWeeklyResp {
  model_id: string;
  brand_id: string;
  brand_name: string;
  model_name: string;
  segment: string | null;
  powertrain_type: string | null;
  week: string;
  week_status?: WeekStatus; // PR 9.2m: 严格 readiness(W23 红线)
  metrics: {
    avg_score: number | null;
    battery_score: number | null;
    cabin_score: number | null;
    driving_score: number | null;
    comfort_score: number | null;
    service_score: number | null;
    smart_driving_score: number | null;
    review_count: number;
    complaint_count: number;
    complaint_count_wow: number | null;
    sentiment_score: number | null;
    risk_score: number;
    risk_level: '低' | '中' | '高' | '重大';
    top_issues: Array<{ category: string; count: number; trend: string }> | null;
    positive_keywords: string[] | null;
    negative_keywords: string[] | null;
    top_positive_quotes: string[] | null;
    top_negative_quotes: string[] | null;
  } | null;
}

interface PeerAvg {
  battery_score: number | null;
  cabin_score: number | null;
  driving_score: number | null;
  comfort_score: number | null;
  service_score: number | null;
  smart_driving_score: number | null;
  _peer_count?: number;
  _peer_scope?: 'strict' | 'powertrain' | 'all' | 'none';
}

const PEER_SCOPE_LABEL: Record<NonNullable<PeerAvg['_peer_scope']>, string> = {
  strict: '同级同动力均值',
  powertrain: '同动力均值',
  all: 'GAC 全车型均值',
  none: '',
};

interface WeeklyTrendPoint {
  week: string;
  avg_score: number | null;
  complaint_count: number;
  review_count: number;
}

interface RelatedRecall {
  recall_id: string;
  announcement_date: string;
  company: string | null;
  vehicle_count: number;
  defect_description: string | null;
  remedy: string | null;
  source_url: string | null;
  is_ota_fix: boolean;
}

export default function ModelDetailPage() {
  const { modelId } = useParams<{ modelId: string }>();
  const { week } = useWeekStore();
  const { isMobile } = useBreakpoint();
  const chartHeight = isMobile ? 260 : 320;

  const { data, isLoading } = useQuery({
    queryKey: ['modelWeekly', modelId, week],
    queryFn: async () =>
      (await api.get<ModelWeeklyResp>(`/models/${modelId}/weekly`, { params: { week } })).data,
    retry: false,
    enabled: Boolean(modelId),
  });

  // Phase 9: 同级平均 (1.3) + 12 周双轴趋势 (1.4) + 关联召回 (1.5)
  const { data: peerAvg } = useQuery({
    queryKey: ['modelPeerAvg', modelId, week],
    queryFn: async () =>
      (await api.get<PeerAvg>(`/models/${modelId}/peer_average`, { params: { week } })).data,
    retry: false,
    enabled: Boolean(modelId),
  });
  const { data: trend } = useQuery({
    queryKey: ['modelWeeklyTrend', modelId],
    queryFn: async () =>
      (await api.get<WeeklyTrendPoint[]>(`/models/${modelId}/weekly_trend`, { params: { weeks: 12 } })).data,
    retry: false,
    enabled: Boolean(modelId),
  });
  const { data: relatedRecalls } = useQuery({
    queryKey: ['modelRelatedRecalls', modelId],
    queryFn: async () =>
      (await api.get<RelatedRecall[]>(`/models/${modelId}/related_recalls`)).data,
    retry: false,
    enabled: Boolean(modelId),
  });

  if (isLoading) return <Skeleton active paragraph={{ rows: 15 }} />;
  // A4: 直接 URL 触发 400(unfinished)/接口异常 → 占位容错(非白屏)
  if (!data) return <MockBanner message={`车型 ${modelId} 不存在 / 本周未结束 / 接口异常`} />;

  // PR 9.2m: not-ready 周(W23 红线)→ 不显硬 0/风险分,改 placeholder(保留面包屑头部)
  if (!isWeekDataReady(data.week_status as WeekStatus | undefined)) {
    return (
      <div className="space-y-5">
        <div className="text-[12px] text-slate-500">
          <Link to="/models" className="hover:underline">车型列表</Link>
          <span className="mx-1.5">/</span>
          <Link to={`/brands/${data.brand_id}`} className="hover:underline">{data.brand_name}</Link>
          <span className="mx-1.5">/</span>
          <span>{data.model_name}</span>
        </div>
        <h1 className="text-[18px] lg:text-[22px] font-semibold text-slate-900 m-0">
          {data.brand_name} {data.model_name} · 车型详情 · {week}
        </h1>
        <Panel title="本周数据">
          <div className="py-12 flex items-center justify-center">
            <Empty description={DATA_NOT_READY_HINT} />
          </div>
        </Panel>
      </div>
    );
  }

  const m = data.metrics;

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 lg:gap-4">
        <div>
          <div className="text-[12px] text-slate-500 mb-1">
            <Link to="/models" className="hover:underline">车型列表</Link>
            <span className="mx-1.5">/</span>
            <Link to={`/brands/${data.brand_id}`} className="hover:underline">{data.brand_name}</Link>
            <span className="mx-1.5">/</span>
            <span>{data.model_name}</span>
          </div>
          <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
            <h1 className="text-[18px] lg:text-[22px] font-semibold text-slate-900 m-0">
              {data.brand_name} {data.model_name} · 车型详情
            </h1>
            {m && <RiskTag level={m.risk_level} />}
          </div>
          <div className="text-[13px] text-slate-600 mt-1">
            {week}
            {m && (
              <>
                <span className="mx-1.5 text-slate-300">·</span>
                综合风险分 <span className="font-semibold text-slate-900">{m.risk_score.toFixed(2)}</span>
                <span className="text-slate-400"> / {m.risk_level}</span>
                {typeof m.complaint_count_wow === 'number' && (
                  <>
                    <span className="mx-1.5 text-slate-300">·</span>
                    较上周投诉{' '}
                    <span
                      className={
                        m.complaint_count_wow > 0
                          ? 'text-rose-600 font-semibold'
                          : m.complaint_count_wow < 0
                            ? 'text-emerald-600 font-semibold'
                            : 'text-slate-600'
                      }
                    >
                      {m.complaint_count_wow > 0 ? '+' : ''}
                      {(m.complaint_count_wow * 100).toFixed(1)}%
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          {(data.segment || data.powertrain_type) && (
            <p className="text-[12px] text-slate-400 mt-1 mb-0">
              {data.segment ?? '—'} · {data.powertrain_type ?? '—'} · {data.brand_name}
            </p>
          )}
        </div>
        {/* Phase 9 P1: 3 个 top buttons (v2.x 占位 disabled + tooltip,导出 primary) */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Tooltip title="v2.x 功能，敬请期待">
            <Button disabled>对比车型</Button>
          </Tooltip>
          <Tooltip title="v2.x 功能，敬请期待">
            <Button disabled>订阅预警</Button>
          </Tooltip>
          <Tooltip title="v2.x 功能，敬请期待">
            <Button type="primary" disabled>导出车型报告</Button>
          </Tooltip>
        </div>
      </div>

      {/* 5 KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
        <StatCard title="综合评分" value={m?.avg_score?.toFixed(2) ?? '—'} hint="平均星级" />
        <StatCard title="本周评论" value={m?.review_count.toLocaleString() ?? '0'} />
        <StatCard title="本周投诉" value={m?.complaint_count.toLocaleString() ?? '0'} />
        <StatCard
          title="情绪分"
          value={m?.sentiment_score !== null && m?.sentiment_score !== undefined ? m.sentiment_score.toFixed(3) : '—'}
          hint="-1 ~ 1"
        />
        <StatCard title="风险分" value={m?.risk_score.toFixed(2) ?? '0'} hint="0-100" />
      </div>

      {/* Hero: 12 周趋势(左 2/3) + 6 维雷达(右 1/3) — Phase 9 P1 重排 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2" title="评分 · 投诉量 · 近 12 周趋势">
          {(() => {
            const t = trend ?? [];
            const s = checkSparsity('trend', t.length);
            return s.ok ? (
              <ReactECharts option={buildWeeklyTrend(t, isMobile)} style={{ height: chartHeight }} />
            ) : (
              <div className="flex items-center justify-center" style={{ height: chartHeight }}>
                <Empty description={s.message} />
              </div>
            );
          })()}
        </Panel>
        <Panel
          title={`6 维评分雷达${
            peerHasData(peerAvg) && peerAvg?._peer_scope
              ? ` · 含${PEER_SCOPE_LABEL[peerAvg._peer_scope] || ''}`
              : ''
          }`}
        >
          {(() => {
            const s = checkSparsity('radar', countRadarDims(m));
            return s.ok ? (
              <ReactECharts option={build6DRadar(m, peerAvg, isMobile)} style={{ height: chartHeight }} />
            ) : (
              <div className="flex items-center justify-center" style={{ height: chartHeight }}>
                <Empty description={s.message} />
              </div>
            );
          })()}
        </Panel>
      </div>

      {/* 关键词 row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Panel title="正面关键词">
          {(() => {
            const kws = m?.positive_keywords ?? [];
            const s = checkSparsity('wordcloud', kws.length);
            return s.ok ? (
              <div className="h-full min-h-[120px] flex items-center">
                <KeywordCloud
                  className="w-full"
                  words={kws.map((w, i) => ({ word: w, weight: 10 - i, polarity: 0.6 }))}
                />
              </div>
            ) : (
              <div className="h-[120px] flex items-center justify-center">
                <Empty description={s.message} />
              </div>
            );
          })()}
        </Panel>
        <Panel title="负面关键词">
          {(() => {
            const kws = m?.negative_keywords ?? [];
            const s = checkSparsity('wordcloud', kws.length);
            return s.ok ? (
              <div className="h-full min-h-[120px] flex items-center">
                <KeywordCloud
                  className="w-full"
                  words={kws.map((w, i) => ({ word: w, weight: 10 - i, polarity: -0.6 }))}
                />
              </div>
            ) : (
              <div className="h-[120px] flex items-center justify-center">
                <Empty description={s.message} />
              </div>
            );
          })()}
        </Panel>
      </div>

      {/* 原文 row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Panel title="典型正面原文">
          <div className="flex flex-col gap-2">
            {(m?.top_positive_quotes ?? []).length === 0 && (
              <span className="text-[12px] text-slate-400">暂无样本</span>
            )}
            {(m?.top_positive_quotes ?? []).slice(0, 3).map((t, i) => (
              <QuoteCard key={i} text={t} sentiment={0.7} />
            ))}
          </div>
        </Panel>
        <Panel title="典型负面原文">
          <div className="flex flex-col gap-2">
            {(m?.top_negative_quotes ?? []).length === 0 && (
              <span className="text-[12px] text-slate-400">暂无样本</span>
            )}
            {(m?.top_negative_quotes ?? []).slice(0, 3).map((t, i) => (
              <QuoteCard key={i} text={t} sentiment={-0.7} />
            ))}
          </div>
        </Panel>
      </div>

      {/* 底部 row: 问题分类(7/12) + 关联召回(5/12) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel className="lg:col-span-7" title="问题分类（本周）">
          {(() => {
            const issues = m?.top_issues ?? [];
            const s = checkSparsity('bar', issues.length);
            return s.ok ? (
              <ReactECharts option={buildIssueBars(issues, isMobile)} style={{ height: chartHeight }} />
            ) : (
              <div className="flex items-center justify-center" style={{ height: chartHeight }}>
                <Empty description={s.message} />
              </div>
            );
          })()}
        </Panel>
        <Panel
          className="lg:col-span-5"
          title={`关联召回事件${relatedRecalls?.length ? ` · ${relatedRecalls.length} 起` : ''}`}
        >
          {(relatedRecalls ?? []).length === 0 ? (
            <div className="h-[280px] flex items-center justify-center">
              <Empty description="本品牌暂无召回事件" />
            </div>
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {(relatedRecalls ?? []).map((r) => (
                <div key={r.recall_id} className="p-3 border border-slate-200 rounded-md">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[12px] text-slate-500">
                        <span>{r.announcement_date}</span>
                        <span>·</span>
                        <span className="truncate">{r.company ?? '—'}</span>
                        {r.is_ota_fix && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 shrink-0">OTA</span>
                        )}
                      </div>
                      <div className="text-[13px] text-slate-800 mt-1">
                        {r.defect_description ?? '—'}
                      </div>
                      {r.remedy && (
                        <div className="text-[12px] text-slate-600 mt-1">
                          <span className="text-slate-400">解决：</span>{r.remedy}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[16px] text-orange-600 font-semibold">
                        {r.vehicle_count.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-slate-500">辆</div>
                      {r.source_url && (
                        <a
                          href={r.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[12px] text-blue-600 hover:underline mt-1 inline-block"
                        >
                          公告 ↗
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function countRadarDims(m: ModelWeeklyResp['metrics']): number {
  if (!m) return 0;
  const scores = [
    m.battery_score,
    m.cabin_score,
    m.driving_score,
    m.comfort_score,
    m.service_score,
    m.smart_driving_score,
  ];
  return scores.filter((s) => s !== null && s !== undefined && s > 0).length;
}

function peerHasData(p: PeerAvg | undefined): boolean {
  if (!p) return false;
  const arr = [
    p.battery_score,
    p.cabin_score,
    p.driving_score,
    p.comfort_score,
    p.service_score,
    p.smart_driving_score,
  ];
  return arr.some((v) => v !== null && v !== undefined && v > 0);
}

function build6DRadar(m: ModelWeeklyResp['metrics'], peer?: PeerAvg, isMobile = false): EChartsOption {
  const values = m
    ? [
        m.battery_score ?? 0,
        m.cabin_score ?? 0,
        m.driving_score ?? 0,
        m.comfort_score ?? 0,
        m.service_score ?? 0,
        m.smart_driving_score ?? 0,
      ]
    : [0, 0, 0, 0, 0, 0];

  // Phase 9 · 1.3: 同级 BEV 紧凑型轿车均值多边形 (灰色虚线)
  const peerSeries = peerHasData(peer)
    ? [
        {
          value: [
            peer!.battery_score ?? 0,
            peer!.cabin_score ?? 0,
            peer!.driving_score ?? 0,
            peer!.comfort_score ?? 0,
            peer!.service_score ?? 0,
            peer!.smart_driving_score ?? 0,
          ],
          name: peer?._peer_scope ? PEER_SCOPE_LABEL[peer._peer_scope] : '对比均值',
          itemStyle: { color: '#94A3B8' },
          lineStyle: { type: 'dashed' as const, color: '#94A3B8' },
          areaStyle: { color: '#94A3B8', opacity: 0.1 },
        },
      ]
    : [];

  return {
    tooltip: { confine: true },
    legend: { show: peerHasData(peer), bottom: 0, textStyle: { fontSize: isMobile ? 10 : 11 } },
    radar: {
      indicator: [
        { name: '三电', max: 5 },
        { name: '座舱', max: 5 },
        { name: '驾驶', max: 5 },
        { name: '舒适', max: 5 },
        { name: '服务', max: 5 },
        { name: '智驾', max: 5 },
      ],
      shape: 'polygon',
      radius: isMobile ? '60%' : '70%',
      splitArea: { show: false },
      axisName: { color: '#475569', fontSize: isMobile ? 10 : 12 },
    },
    series: [
      {
        type: 'radar',
        data: [
          {
            value: values,
            name: '本周',
            itemStyle: { color: '#3B82F6' },
            areaStyle: { color: '#3B82F6', opacity: 0.2 },
          },
          ...peerSeries,
        ],
      },
    ],
  };
}

function buildWeeklyTrend(trend: WeeklyTrendPoint[], isMobile = false): EChartsOption {
  const weeks = trend.map((t) => t.week);
  const scores = trend.map((t) => t.avg_score);
  const counts = trend.map((t) => t.complaint_count);
  return {
    tooltip: { trigger: 'axis', confine: true },
    legend: { data: ['评分', '投诉量'], bottom: 0, textStyle: { fontSize: isMobile ? 10 : 11 } },
    grid: { left: isMobile ? 36 : 50, right: isMobile ? 38 : 50, top: 20, bottom: isMobile ? 52 : 40 },
    xAxis: {
      type: 'category',
      data: weeks,
      axisLabel: { fontSize: isMobile ? 9 : 10, color: '#64748B', rotate: isMobile ? 45 : 0 },
    },
    yAxis: [
      {
        type: 'value',
        name: '评分',
        min: 0,
        max: 5,
        axisLabel: { fontSize: isMobile ? 9 : 10, color: '#3B82F6' },
        splitLine: { lineStyle: { color: '#F1F5F9' } },
      },
      {
        type: 'value',
        name: '投诉量',
        axisLabel: { fontSize: isMobile ? 9 : 10, color: '#EA580C' },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: '评分',
        type: 'line',
        yAxisIndex: 0,
        data: scores,
        smooth: true,
        itemStyle: { color: '#3B82F6' },
        connectNulls: true,
      },
      {
        name: '投诉量',
        type: 'bar',
        yAxisIndex: 1,
        data: counts,
        itemStyle: { color: '#EA580C', opacity: 0.7 },
        barWidth: '40%',
      },
    ],
  };
}

function buildIssueBars(issues: Array<{ category: string; count: number; trend: string }>, isMobile = false): EChartsOption {
  return {
    grid: { left: isMobile ? 60 : 80, right: 30, top: 16, bottom: 30 },
    tooltip: { trigger: 'axis', confine: true },
    xAxis: { type: 'value', axisLabel: { fontSize: isMobile ? 9 : 10, color: '#64748B' } },
    yAxis: {
      type: 'category',
      data: issues.map((i) => i.category),
      axisLabel: { fontSize: isMobile ? 9 : 11, color: '#475569' },
    },
    series: [
      {
        type: 'bar',
        data: issues.map((i) => i.count),
        itemStyle: { color: '#EA580C' },
        label: { show: true, position: 'right', fontSize: 11, color: '#475569' },
      },
    ],
  };
}
